import React, { useMemo } from 'react';
import { marked } from 'marked';
import moment from 'moment';

import { convertFromCents } from '../../../helpers/currency';
import { JobSchema } from '../../../schema/job';

const JobPosting = ({ job }) => {
  const description = useMemo(() => {
    if (!job.description) return job.title;

    return marked(job.description);
  }, [job]);

  const employmentType = useMemo(() => {
    const { employmentTypes = [] } = job;

    return employmentTypes.map((type) => {
      switch (type) {
        case 'full_time':
        case 'part_time':
        case 'contractor':
        case 'temporary':
        case 'intern':
        case 'volunteer':
        case 'per_diem':
          return type.toUpperCase();
        default:
          return 'OTHER';
      }
    });
  }, [job]);

  const remotePosition = useMemo(() => {
    const { locations = [] } = job;

    return !!locations.find((location) => location.placeId === 'remote');
  }, [job]);

  const jobLocationType = useMemo(() => (remotePosition ? 'TELECOMMUTE' : undefined), [remotePosition]);
  const jobLocation = useMemo(() => {
    const { locations = [] } = job;

    const physicalLocations = locations.filter((location) => location.placeId !== 'remote');

    if (physicalLocations.length === 0) {
      if (job?.organization?.locations?.length === 1 && job.organization.locations[0]?.placeId !== 'remote') {
        physicalLocations.push(job.organization.locations[0]);
      } else {
        return undefined;
      }
    }

    const result = physicalLocations.map((location) => ({
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: location.name,
      },
    }));

    return result.length === 1 ? result[0] : result;
  }, [job]);

  const baseSalary = useMemo(() => {
    if (!job.compensationPublic) return undefined;

    return {
      '@type': 'MonetaryAmount',
      currency: job.compensationCurrency,
      value: {
        '@type': 'QuantitativeValue',
        minValue: convertFromCents(job.compensationAmountMinCents),
        maxValue: convertFromCents(job.compensationAmountMaxCents),
        unitText: job.compensationPeriod?.toUpperCase(),
      },
    };
  }, [job]);

  const applicantLocationRequirements = useMemo(() => {
    if (!remotePosition) return undefined;

    return {
      '@type': 'Country',
      name: 'Earth',
    };
  }, [remotePosition]);

  const hiringOrganization = useMemo(() => {
    const { organization, locations: jobLocations } = job;
    const domain = organization.domain ? `https://${organization.domain}` : undefined;
    const keywords = organization.industryTags
      ?.map(({ description: itDescription }) => itDescription.replace(/,/g, ''))
      ?.join(', ');

    let organizationLocations = organization.locations.length === 0 ? jobLocations : organization.locations;
    organizationLocations = organizationLocations
      ?.filter((location) => location.placeId !== 'remote')
      ?.map((location) => ({
        address: {
          '@type': 'PostalAddress',
          addressLocality: location.name,
        },
      }));

    return {
      '@type': 'Organization',
      name: organization.name,
      description: organization.description,
      numberOfEmployees: organization.approxEmployees,
      address: organizationLocations,
      sameAs: domain,
      url: domain,
      logo: organization.logoUrl,
      keywords,
    };
  }, [job]);

  const jobPosting = useMemo(() => {
    const { title, postedAt: datePosted, status } = job;

    let validThrough = moment().add(90, 'days').format('YYYY-MM-DD');

    if (status === 'active' && job.expiresAt !== null) {
      validThrough = job.expiresAt;
    } else if (['closed', 'expired', 'deactivated'].includes(status)) {
      const dates = [job.expiresAt, job.closedAt, job.deactivatedAt]
        .filter((date) => date !== null)
        .map((date) => moment(date));

      if (dates.length > 0) {
        validThrough = moment.min(dates).format('YYYY-MM-DD');
      } else {
        validThrough = '2024-01-01';
      }
    }

    return {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      title,
      description,
      datePosted,
      validThrough,
      employmentType,
      hiringOrganization,
      jobLocationType,
      jobLocation,
      applicantLocationRequirements,
      baseSalary,
    };
  }, [
    job,
    description,
    employmentType,
    jobLocationType,
    jobLocation,
    baseSalary,
    applicantLocationRequirements,
    hiringOrganization,
  ]);

  if (!jobPosting?.jobLocation && jobLocationType !== 'TELECOMMUTE') {
    return null;
  }

  return (
    <script
      data-testid="job-ldjson"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPosting) }}
      async
    />
  );
};

JobPosting.propTypes = {
  job: JobSchema.isRequired,
};

export default JobPosting;
